import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { useQueryClient } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { Input, InputProps } from '~/shared/components/form';
import {
    addressSuggestionsQuery,
    isValidAddressQuery,
    useAddressAutocomplete,
} from '~/shared/hooks/useAddressAutocomplete/useAddressAutocomplete';
import { AddressSuggestion } from '~/shared/hooks/useAddressAutocomplete/api/models';
import styles from './AddressAutocomplete.module.scss';

const DEBOUNCE_MS = 300;

/** Keeps one building's addresses; text search also matches numbers sharing a prefix. */
const inBuilding = (suggestions: Array<AddressSuggestion>, buildingId: string) =>
    suggestions.filter(({ data }) => data.adgangsadresseid === buildingId);

type AddressAutocompleteProps = InputProps & {
    /** Called with the picked address or building. */
    onSelectAddress(suggestion: AddressSuggestion): void;
};

/** Address field with suggestions (IMC-3267). No listbox ARIA: unlinked, it reads worse. */
export const AddressAutocomplete = ({
    onSelectAddress,
    ...inputProps
}: AddressAutocompleteProps) => {
    const { setValue } = useFormContext();
    const queryClient = useQueryClient();
    const value = String(useWatch({ name: inputProps.name }) ?? '');

    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    // Starts on the first row, so Enter picks it.
    const [focusedIndex, setFocusedIndex] = useState(0);
    /** Picked building and the query listing its addresses. */
    const [narrowedToBuilding, setNarrowedToBuilding] = useState<{
        query: string;
        id: string;
    } | null>(null);
    /** Last value the customer typed; prefill, lookups and our picks never set it. */
    const typedValue = useRef<string | null>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);

    /** Whether the browser autofilled the field. */
    const isAutofilled = () => {
        const input = fieldRef.current?.querySelector('input');

        try {
            return input?.matches(':-webkit-autofill') === true;
        } catch {
            // Unsupported pseudo-classes throw.
            return false;
        }
    };

    useDebounce(
        () => {
            // Only search what the customer typed; a code write since then no longer matches.
            if (value !== typedValue.current) return;

            typedValue.current = null;

            // Autofill already wrote a complete address; do not open the list over it.
            if (isAutofilled()) {
                setIsOpen(false);
                return;
            }

            // Keep trailing space: "Smedeholm " lists house numbers, "Smedeholm" lists streets.
            const nextQuery = value.trimStart();

            setQuery(nextQuery);
            setFocusedIndex(0);
            // Close below the minimum, or the kept previous list would stay visible.
            setIsOpen(isValidAddressQuery(nextQuery));
        },
        DEBOUNCE_MS,
        [value],
    );

    const { data = [], isFetching, isLoading, isPlaceholderData } = useAddressAutocomplete(query);

    // After a building pick, list only its addresses (not the kept previous list).
    const suggestions =
        narrowedToBuilding && query === narrowedToBuilding.query && !isPlaceholderData
            ? inBuilding(data, narrowedToBuilding.id)
            : data;

    // Keep the arrow-key row in view once the list scrolls.
    useEffect(() => {
        listRef.current?.children[focusedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [focusedIndex]);

    const close = () => {
        setIsOpen(false);
        setFocusedIndex(0);
    };

    const apply = async (suggestion: AddressSuggestion) => {
        // A pick replaces whatever was typed, so the pending debounce must not search it.
        typedValue.current = null;

        if (suggestion.type === 'adresse') {
            close();
            onSelectAddress(suggestion);
            return;
        }

        const nextQuery = suggestion.tekst.trimStart();
        const buildingId = suggestion.type === 'adgangsadresse' ? suggestion.data.id : undefined;

        setFocusedIndex(0);

        // A street is not an address yet; search within it.
        if (!buildingId) {
            setNarrowedToBuilding(null);
            setValue(inputProps.name, suggestion.tekst, { shouldValidate: true });
            setQuery(nextQuery);
            setIsOpen(isValidAddressQuery(nextQuery));
            return;
        }

        // A building has street, number, postcode and city, so fill the form now.
        setIsOpen(false);
        onSelectAddress(suggestion);
        setNarrowedToBuilding({ query: nextQuery, id: buildingId });
        setQuery(nextQuery);

        try {
            const addresses = inBuilding(
                await queryClient.fetchQuery(addressSuggestionsQuery(nextQuery)),
                buildingId,
            );

            // Houses are done; flats offer floors and doors as an optional refinement.
            setIsOpen(addresses.length > 1);
        } catch {
            // The form is already filled at building level.
        }
    };

    const moveFocus = (offset: number) => {
        const last = suggestions.length - 1;
        const next = focusedIndex + offset;

        setFocusedIndex(next > last ? 0 : next < 0 ? last : next);
    };

    // Only the first lookup opens an empty box; a no-match search leaves it closed.
    const isListVisible = isOpen && (suggestions.length > 0 || isLoading);

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // List closed: Enter submits as normal.
        if (!isListVisible) return;

        if (event.key === 'Escape') {
            close();
        } else if (event.key === 'Enter') {
            // Pick the focused row instead of submitting and leaving the step.
            if (suggestions[focusedIndex]) apply(suggestions[focusedIndex]);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!suggestions.length) return;

            moveFocus(event.key === 'ArrowDown' ? 1 : -1);
        } else {
            return;
        }

        event.preventDefault();
    };

    return (
        // Close on blur of the whole field, leaving the input's own blur handler alone.
        // `input` events come only from the customer (typing, paste, autofill), never setValue.
        <div
            ref={fieldRef}
            onBlur={close}
            onInput={({ target }) => {
                if (target instanceof HTMLInputElement) typedValue.current = target.value;
            }}
        >
            <Input
                {...inputProps}
                onKeyDown={onKeyDown}
                // Fake type: Chrome ignores "off" on address fields; this hides its saved addresses.
                autoComplete="transaction-amount"
            >
                {/* Between input and error; the zero-height anchor stops the error moving the list. */}
                {isListVisible && (
                    <div className={styles.anchor}>
                        <ul
                            ref={listRef}
                            className={`${styles.suggestions}${
                                isFetching ? ` ${styles.loading}` : ''
                            }`}
                        >
                            {suggestions.map((suggestion, index) => (
                                <li
                                    key={`${suggestion.type}-${suggestion.tekst}`}
                                    className={`${styles.suggestion}${
                                        index === focusedIndex ? ` ${styles.selected}` : ''
                                    }`}
                                    // mousedown, so the pick lands before the field blurs.
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        apply(suggestion);
                                    }}
                                >
                                    {suggestion.forslagstekst}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Input>
        </div>
    );
};
